import { useState } from "react";
import { CalendarClock, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { http, money, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#EC5B24]";

const Updates = ({ b }) => (
  <div className="space-y-2">
    {[...(b.flight_updates || [])].reverse().map((u, i) => (
      <div key={i} className={`rounded-xl p-3 text-sm ${u.kind === "delay" ? "bg-amber-50 text-amber-800" : "bg-indigo-50 text-indigo-800"}`} data-testid={`flight-update-${i}`}>
        <b>{u.kind === "delay" ? "Flight delayed" : "Flight rescheduled"}</b> · new departure <b>{u.new_departure.replace("T", " ")}</b>{u.flight_no ? ` · flight ${u.flight_no}` : ""}{u.message ? <div className="mt-1">{u.message}</div> : null}
      </div>
    ))}
  </div>
);

const FeePay = ({ b, reload }) => {
  const { config } = useAuth();
  const [utr, setUtr] = useState("");
  const fee = b.change_fee;
  const url = `upi://pay?${new URLSearchParams({ pa: config.upi_id || "", pn: config.payee_name || "WanderNest", am: fee.amount.toFixed(2), cu: "INR", tn: `Change fee ${b.booking_id}` })}`;
  const send = () => http.post(`/bookings/${b.booking_id}/change-fee-utr`, { utr }).then(() => { toast.success("Submitted. We'll confirm shortly."); reload(); }).catch((e) => toast.error(errMsg(e)));
  if (fee.status === "paid") return <div className="text-sm text-[#0F9D58] font-semibold" data-testid="change-fee-paid">Change fee {money(fee.amount)} received. Thank you!</div>;
  if (fee.status === "submitted") return <div className="text-sm text-blue-700" data-testid="change-fee-submitted">Change fee payment under verification (UTR {fee.utr}).</div>;
  return (
    <div className="rounded-xl border border-dashed p-3 space-y-2" data-testid="change-fee-due">
      <div className="text-sm font-semibold">{fee.status === "rejected" ? "We couldn't find your payment. " : ""}Change fee due: <span className="font-mono">{money(fee.amount)}</span></div>
      <a href={url} data-testid="change-fee-upi-btn" className="inline-flex items-center gap-2 rounded-full bg-[#EC5B24] text-white font-semibold px-4 py-2 text-sm"><Smartphone className="h-4 w-4" /> Pay with UPI app</a>
      <div className="text-xs text-slate-500">Or pay to UPI ID {config.upi_id}, then enter the transaction ID:</div>
      <div className="flex gap-2">
        <input data-testid="change-fee-utr-input" value={utr} onChange={(e) => setUtr(e.target.value.replace(/\s/g, ""))} placeholder="12-digit UTR" className={`${inputCls} font-mono`} />
        <button data-testid="change-fee-utr-submit" disabled={utr.length < 6} onClick={send} className="rounded-xl bg-[#1C1A4A] text-white font-semibold px-4 disabled:opacity-50">Submit</button>
      </div>
    </div>
  );
};

const RequestForm = ({ b, reload }) => {
  const [f, setF] = useState({ new_date: "", preferred_time: "Any time", note: "" });
  const [open, setOpen] = useState(false);
  const send = () => http.post(`/bookings/${b.booking_id}/change-request`, f).then(() => { toast.success("Request sent. We'll check new flights and get back to you."); reload(); }).catch((e) => toast.error(errMsg(e)));
  if (!open) return <button data-testid="change-request-open-btn" onClick={() => setOpen(true)} className="rounded-full border px-4 py-2 text-sm font-semibold">Request date change</button>;
  return (
    <div className="grid sm:grid-cols-3 gap-2" data-testid="change-request-form">
      <input data-testid="change-new-date" type="date" min={new Date().toISOString().slice(0, 10)} value={f.new_date} onChange={(e) => setF({ ...f, new_date: e.target.value })} className={inputCls} />
      <select data-testid="change-preferred-time" value={f.preferred_time} onChange={(e) => setF({ ...f, preferred_time: e.target.value })} className={`${inputCls} bg-white`}>
        {["Any time", "Morning", "Afternoon", "Evening", "Night"].map((x) => <option key={x}>{x}</option>)}
      </select>
      <input data-testid="change-note" value={f.note} maxLength={300} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (optional)" className={inputCls} />
      <p className="sm:col-span-3 text-xs text-slate-500">Airline change charges and any fare difference apply. We'll tell you the amount before changing.</p>
      <button data-testid="change-request-submit" disabled={!f.new_date} onClick={send} className="sm:col-span-3 rounded-xl bg-[#EC5B24] text-white font-semibold py-2.5 disabled:opacity-50">Send request</button>
    </div>
  );
};

export const ChangeRequest = ({ b, reload }) => {
  const cr = b.change_request;
  const canAsk = ["payment_verified", "ticketed"].includes(b.status) && cr?.status !== "requested";
  if (!canAsk && !cr && !b.flight_updates?.length && !b.change_fee) return null;
  return (
    <div className="bg-white rounded-2xl border mt-4 p-5 space-y-3" data-testid="change-section">
      <div className="flex items-center gap-2 font-heading font-bold"><CalendarClock className="h-5 w-5 text-[#EC5B24]" /> Delay & date change</div>
      <Updates b={b} />
      {cr && <div className="text-sm text-slate-600" data-testid="change-request-status">Date change to {cr.new_date} ({cr.preferred_time}): <b>{cr.status === "requested" ? "we're checking flights" : cr.status === "done" ? "Done" : "Declined"}</b>{cr.reason ? ` — ${cr.reason}` : ""}</div>}
      {b.change_fee && <FeePay b={b} reload={reload} />}
      {canAsk && <RequestForm b={b} reload={reload} />}
    </div>
  );
};
